import { useRef, useState, useEffect } from 'react';
import { AuthContext } from "../../../components/AuthWrapper";
import { useNavigate, useOutletContext, useParams, } from "react-router-dom";
import { ContentState, convertFromHTML, convertToRaw } from "draft-js";
import { AcademicClass, Activity, Campus, College, Gender, PersonalInterest, Pronouns, Race, ReferenceItemDTO, ReferenceType, Semester, StudentAward, StudentAwardDTO, StudentAwardProfile, StudentProfile, YesNoMaybeAlready } from '../../../api/client';
import ScrollButtons from '../../../components/ScrollButtons';
import StudentNotes from '../../../components/StudentNotes';
import { Button } from '../../../components/UiComponents/button';
import PageTitle from '../../../components/PageTitle';
import { Heading } from '../../../components/UiComponents/heading';
import { Container } from '../../../components/Container';
import { SkeletonLoader } from '../../../components/NotAuthorized/SkeletonLoader';
import { NotSignedIn } from '../../../components/NotAuthorized/NotSignedIn';
import { ErrorPage } from '../../../components/NotAuthorized/Error';
import { NotAdminAuthorized } from '../../../components/NotAuthorized/NotAdminAuthorized';
import { StudentProfileCard } from './StudentProfileCard';
import { DefaultProfileNonReportedField, formatCitizenCodes, formatPronouns, StudentAwardCard, StudentCardSectionHeader, StudentExperienceCard, StudentProfileRowFormat, StudentTagFormat } from './StudentProfileHelpers';
import { booleanFormatter, formatSemester, YesNoMaybeFormatter } from './StudentDataFormatter';
import { Hr } from '../../../components/Hr';
import { downloadResume, normalizeNetId, printGradSchoolPlans } from '../../../helpers/stringHelpers';
import { TwoColumnGrid } from './TwoColumnGrid';
import { CaretDownFill, CaretUpFill, PlusCircle } from 'react-bootstrap-icons';
import AddStudentAwardModal, { StudentAwardForm } from '../AwardsPrograms/AddStudentAwardForm';
import toast from 'react-hot-toast';

export const StudentDetailPage: React.FC = () => {
  const { usfid } = useParams<{ usfid: string }>();
  const referenceDescription = useRef<HTMLDivElement>(null);
  const textEditorDefault = "";
  const initialValues = { description: "" };
  const [nonce, setNonce] = useState<number>(Date.now());
  const { client, state, student, loggedIn }: AuthContext = useOutletContext();
  const [studentData, setStudentData] = useState<StudentProfile | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  
  const [studentAwards, setStudentAwards] = useState<StudentAwardProfile[]>([]);

  const [awards, setAwards] = useState<ReferenceItemDTO[] | undefined>(); // holds list of awards
  const [programs, setPrograms] = useState<ReferenceItemDTO[] | undefined>(); // holds list of programs
  const [colleges, setColleges] = useState<College[] | undefined>(); // holds list of colleges

  const [awardModalOpen, setAwardModalOpen] = useState<boolean>(false); // whether studentAward modal is open or not
  const [awardModalLoading, setAwardModalLoading] = useState<boolean>(false); // When trying to open the award modal, wait for fetchData to complete
  const [editingAward, setEditingAward] = useState<StudentAward | null>(null); // holds the award currently being edited

  const [orderAwardsAscending, setOrderAwardsAscending] = useState<boolean>(false); // show student awards ascending or not when displaying them


  const blocksFromHTMLDescription = convertFromHTML(
    initialValues.description || textEditorDefault
  );
  const stateDescription = ContentState.createFromBlockArray(
    blocksFromHTMLDescription.contentBlocks,
    blocksFromHTMLDescription.entityMap
  );
  (referenceDescription.current as any)?.setContentState(convertToRaw(stateDescription));
  const navigate = useNavigate();

  useEffect(() => {
    if (!client) return;

    if (!loggedIn) {
      setLoading(false);
      return;
    }

    if (usfid) {
      setLoading(true);
      client.student_GetStudent(usfid)
        .then((fetchedData) => {
          setStudentData(fetchedData);
		  if(studentData?.awardList) setStudentAwards(studentData?.awardList)
          setError(null);
        })
        .catch((error) => {
          setError(`${error.status}. Either the student did not complete their profile form or your link is wrong.`);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setError('No USF ID provided');
    }

  }, [client, usfid, loggedIn, student]);


  // Make sure the studentawards data is populated correctly
  // Award data seems to sometimes be empty without this
  useEffect(() => {
	if (studentData?.awardList) setStudentAwards(studentData?.awardList)
  }, [studentData])

	// When we attempt to open the award modal, check if we've already grabbed award/program/colleges from the backend. If not, populate them first then open the modal.
	const handleAddAwardModal = async () => {
		await fetchData()
		setAwardModalOpen(true);
	}

	// When edit is clicked on any award. Same as above; check if we have needed data then grab award information into the state and open the modal.
	const handleEditAward = async (award: StudentAward) => {
		await fetchData();

		setEditingAward(award);
		setAwardModalOpen(true);
	}

  // Fetch data for StudentAwardModal, should only be called if the user attempts to open the modal. Otherwise, no reason to grab this data.
	const fetchData = async () => {
		// Wait for user and admin state to load, otherwise the backend will throw authentication errors
		if (state && (!state.user || !state.user.admin)) return;

		// If any of these are not populated, we should try and grab the data.
		if (!awards || !programs || !colleges) {
			setAwardModalLoading(true);
			
			const fetchedAwards = await client?.admin_GetReferenceItems(ReferenceType.Award);
			const fetchedPrograms = await client?.admin_GetReferenceItems(ReferenceType.Program);
			const fetchedColleges = await client?.admin_GetColleges();
			
			setAwards(fetchedAwards);
			setPrograms(fetchedPrograms);
			setColleges(fetchedColleges);

			setAwardModalLoading(false);	
		}
	};

	// When we submit the award form, create the model, then distinguish if we are editing an existing award or not
	const handleAwardSubmit = async (form: StudentAwardForm) => {
		const normalizedID = normalizeNetId(form.netId!.trim());

    	const model = new StudentAwardDTO({
    	  netID: normalizedID,
    	  startAcademicYear: form.startAcademicYear!,
    	  collegeId: form.collegeId!,
    	  awardId: form.awardId!,
    	  awardStatus: form.awardStatus,
    	  programId: form.programId!,
    	  programDetails: form.programDetails,
    	  isUndergraduate: form.isUndergraduate,
    	  estimatedAmount: form.estimatedAmount == null ? undefined : form.estimatedAmount,
    	})

		if (editingAward) {
			let id = editingAward.id // Not sure if this is necessary, but it is possible we clear the state and lose the id by the time the backend responds; this could help prevent it
			await client?.admin_EditStudentAward(id, model)
			.then((res) => {
				toast.success("Award successfully updated!")
				setStudentAwards((prev) => [...prev.filter((a) => a.id != id), res]) //Remove the outdated version, then add the updated version returned from the backend
			})
			.catch((error) => {
				console.log("An error occured when editing award: ", error.response);
    		    toast.error("An error occurred when attempting to update this award.", {duration: 3000});
    		    throw error
			})
		} else {
    		await client?.admin_AddStudentAward(model)
    		  .then((res) => {
    		    toast.success("Award successfully added!")
				setStudentAwards((prev) => [...prev, res]); // Update state to add response from backend
    		  })
    		  .catch((error) => {
				console.log("Error when adding award: ", error)
    		    if (error.response && error.status === 404) {
    		      toast.error("Unable to find a student with that UID", {duration: 3000});
    		    } else {
    		      toast.error("An error occurred when attempting to add this award.", {duration: 3000});
    		    }
    		    throw error
    		});
		}
	}

	// Flips isDeleted on the award to true. Does not call backend method again since we can just filter it out on the front-end
	const handleDeleteStudentAward = async (id: string) => {
		await client?.admin_DeleteStudentAward(id)
		.then(() => {
			toast.success("Award successfully removed!")
			setStudentAwards((prev) => prev?.filter((a) => a.id != id));
		})
		.catch((error) => {
			console.log("Error occurred when deleting award: ", error.response);
    	    toast.error("An error occurred when attempting to delete this award.", {duration: 3000});
    	    throw error
    	});
  	}

	useEffect(() => {
		console.log("awards: ", studentAwards);
	}, studentAwards)

  if (!loggedIn) return <NotSignedIn/>
  if (loading) return <SkeletonLoader/>

  // Explanation of Admin Authorization below: 
  // If the user doesn't have a student object, then this is not their profile, and unless they're an admin, they should not be able to view it.
  // If they are not an admin on top of that, then they should not be authorized to view this page.
  if (state.user && !state.user.student && !state.user.admin) return <NotAdminAuthorized />
  
  if (error) return <ErrorPage error={error} />

	const undergradGPA = 
		studentData?.undergradGPA === 0 ? "4.0 (Student currently in their first semester, so default GPA is 4.0)"
		: studentData?.undergradGPA ? studentData.undergradGPA 
		: "N/A (Student did not go to USF for Undergraduate studies)";
	const gradGPA = 
		studentData?.gradGPA === 0 ? "4.0 (Student currently in their first semester)" 
		: studentData?.gradGPA ? studentData.gradGPA 
		: "N/A (Student does not go to USF for Graduate studies)";

	const printSpeakForeignLanguage = booleanFormatter(studentData?.speakForeignLanguageList ? studentData.speakForeignLanguageList.length > 0 : false);


  return (
    <Container>
      {studentData && <div className="flex flex-col align-start lg:w-10/12 justify-self-center lg:mt-0">
        <PageTitle title={`${studentData?.user?.firstName} ${studentData?.user?.lastName} - Student Profile`} />
        <Heading text="Student Profile" />

        <div className="flex flex-row lg:justify-between justify-around ">
          <h1 className="text-xl lg:text-3xl font-bold mb-6">
            {studentData?.user?.firstName} {studentData?.user?.lastName}
          </h1>
          {student && student.id == usfid && <Button
            onClick={() => navigate(`/profile/edit/${usfid}`)}
            variant="default"
            size="lg"
			abbr="Click to edit profile"
          >
            Edit
          </Button>}
        </div>

        <div className='grid grid-rows lg:grid-cols-2 gap-8'> {/* Cards stack vertically when screen is small */}
          {/* Left Card */}
          <StudentProfileCard title='Student Overview'>
			<StudentCardSectionHeader fromDW/>
			<TwoColumnGrid>
				<StudentProfileRowFormat field="Email" value={studentData.user?.email ?? "Unknown"} />
			</TwoColumnGrid>

			<div className='my-2 flex items-center justify-center'><Hr /></div>

			<StudentCardSectionHeader />
			<TwoColumnGrid>
				{/* Personal Info */}
				<StudentProfileRowFormat field="Preferred Name" value={studentData.preferredName?.trim() != "" ? studentData.preferredName : "No Preference"} />
				<StudentProfileRowFormat field="Pronouns" value={studentData.preferredPronouns && studentData?.preferredPronouns != Pronouns.NotProvided ? formatPronouns(studentData.preferredPronouns) : DefaultProfileNonReportedField} />
				<StudentProfileRowFormat field="LinkedIn" value={studentData.linkedinProfile?.trim() != "" && studentData.linkedinProfile != undefined ? studentData.linkedinProfile : DefaultProfileNonReportedField} />
			</TwoColumnGrid>
          </StudentProfileCard>
          
          {/* Right Card */}
          <StudentProfileCard title='Academic Summary'>
			<StudentCardSectionHeader fromDW />
			<TwoColumnGrid>
				<StudentProfileRowFormat field="USF ID" value={studentData.user?.usfid ?? "Unknown"} />
				<StudentProfileRowFormat field="Entry Term" value={`${formatSemester(studentData.entryTerm?.semester)} ${studentData.entryTerm?.year}`} />
			</TwoColumnGrid>

			<div className='my-2 flex items-center justify-center'><Hr /></div>

			<StudentCardSectionHeader />
			<TwoColumnGrid>
				<StudentProfileRowFormat field="Expected Graduation" value={studentData.expectedGraduationSemester != Semester.NotProvided ? `${formatSemester(studentData.expectedGraduationSemester)} ${studentData.expectedGraduationYear}` : DefaultProfileNonReportedField} />
				<StudentProfileRowFormat field="Resume" 
					value={studentData.resumeId ? 
						<Button
							variant={"default"}
							onClick={() => downloadResume(studentData.resumeId, client)}
							abbr="Click to download file"
						>
							Download Resume
						</Button> : <div>Not uploaded</div>} 
				/>
			</TwoColumnGrid>
          </StudentProfileCard>
        </div>

        <StudentProfileCard title='Academic Profile' cardStyling='mt-8'>
			<StudentCardSectionHeader fromDW />
			<TwoColumnGrid>
				<StudentProfileRowFormat field="Campus" value={studentData.campus ? Campus[studentData.campus] : "N/A"} />
				<StudentProfileRowFormat field="College" value={studentData.college} />
				<StudentProfileRowFormat field="Major(s)" 
					value={
						[studentData.major1, studentData.major2, studentData.major3]
							.filter((major): major is string => major != null && major.trim() !== "")
							.join(", ").toString()
					} />
				<StudentProfileRowFormat field="Minor(s)" 
					value={
						<>
							{(studentData.minor1 || studentData.minor2 || studentData.minor3) ? [studentData.minor1, studentData.minor2, studentData.minor3]
								.filter((minor): minor is string => minor != null && minor.trim() !== "")
								.join(", ").toString() : "None"}
						</>
					} />
				<StudentProfileRowFormat field="Academic Level" value={studentData.isUndergraduateStudent ? "Undergraduate" : "Graduate"} />
				<StudentProfileRowFormat field="Academic Standing" value={studentData.academicClass ? AcademicClass[studentData.academicClass] : "Unknown"} />
				<StudentProfileRowFormat field="Undergraduate GPA" value={undergradGPA} />
				<StudentProfileRowFormat field="Graduate GPA" value={gradGPA} />
				<StudentProfileRowFormat field="Academic Tags" 
					value={
						<>
							{studentData.isFTICStudent && <StudentTagFormat value="FTIC" />}
							{studentData.isHonorsStudent && <StudentTagFormat value="JGHC Honors" />}
							{studentData.isHonorsDepartmentProgram && <StudentTagFormat value="Departmental Honors" />}

							{/* BASIC LOGIC TO DISPLAY EMPTY CATEGORY, PLEASE ADD MORE CATEGORIES AS NEEDED */}
							{!studentData.isFTICStudent	&& !studentData.isHonorsStudent && !studentData.isHonorsDepartmentProgram && "None"}
						</>
					} />
			</TwoColumnGrid>
        </StudentProfileCard>

		<StudentProfileCard title='Programs, Scholarships, and Financial Aid' cardStyling='mt-8'>
			<StudentCardSectionHeader fromDW />
			<TwoColumnGrid>
				<StudentProfileRowFormat field="Programs" 
					value={
						<>
							{studentData.futureStrategies && <StudentTagFormat value="GNSI Future Strategists" />}
							{studentData.wilcoxScholars && <StudentTagFormat value="Wilcox Scholars" />}
							{studentData.peaceCorpPrep && <StudentTagFormat value="Peace Corps Prep" />}

							{/* BASIC LOGIC TO DISPLAY EMPTY CATEGORY, PLEASE ADD MORE CATEGORIES AS NEEDED */}
							{!studentData.futureStrategies && !studentData.wilcoxScholars && !studentData.peaceCorpPrep && "None"}
						</>
					} />

				<StudentProfileRowFormat field="Scholarships" 
					value={
						<>
							{studentData.kosoveSociety && <StudentTagFormat value="Kosove Society" />}
							{studentData.isNationalMeritScholar && <StudentTagFormat value="National Merit Scholarship" />}

							{/* BASIC LOGIC TO DISPLAY EMPTY CATEGORY, PLEASE ADD MORE CATEGORIES AS NEEDED */}
							{!studentData.kosoveSociety && !studentData.isNationalMeritScholar && "None"}
						</>
					} />
				
				<StudentProfileRowFormat field="Financial Aid" 
					value={
						<>
							{studentData.isFinancialAidStudent && <StudentTagFormat value="Kosove Society" />}
							{studentData.fwspAwarded && 
								<>
									<StudentTagFormat value={`Federal Work Study ($${studentData.fwspAwarded} Awarded, $${studentData.fwspReceived} Received)`} />
								</>}
							{studentData.isPellGrantRecipient && <StudentTagFormat value="Pell Grant" />}
							
							{/* BASIC LOGIC TO DISPLAY EMPTY CATEGORY, PLEASE ADD MORE CATEGORIES AS NEEDED */}
							{!studentData.isFinancialAidStudent && !studentData.fwspAwarded && !studentData.isPellGrantRecipient && "None"}
						</>
					} />
			</TwoColumnGrid>
        </StudentProfileCard>

		<StudentProfileCard title='Background and Institutional Information' cardStyling='mt-8'>
			<StudentCardSectionHeader fromDW />
			<TwoColumnGrid>
				{/* Logistics Info */}
				<StudentProfileRowFormat field="Race" value={studentData.race ? Race[studentData.race] : "Unknown"} />
				<StudentProfileRowFormat field="Gender" value={studentData.gender ? Gender[studentData.gender] : "Unknown"} />
				<StudentProfileRowFormat field="Citizenship Status" value={studentData.citizenshipStatus ? formatCitizenCodes(studentData.citizenshipStatus) : "Unknown"} />
				<StudentProfileRowFormat field="Permanent Address" 
					value={
						(studentData.addressCity || studentData.addressState || studentData.addressCountry) ?
							[studentData.addressCity, studentData.addressState, studentData.addressCountry]
								.filter((item): item is string => item != null && item.trim() !== "")
								.join(", ").toString()
						: "Unknown"} />
				<StudentProfileRowFormat field="Additional Specifications" 
					value={
						<>
							{studentData.isTransferStudent && <StudentTagFormat value="Transfer Student" />}
							{studentData.isAthelete && <StudentTagFormat value="Athelete" />}
							{studentData.isVeteran && <StudentTagFormat value="Veteran" />}

							{/* BASIC LOGIC TO DISPLAY EMPTY CATEGORY, PLEASE ADD MORE CATEGORIES AS NEEDED */}
							{!studentData.isTransferStudent && !studentData.isAthelete && !studentData.isVeteran && "None"}
						</>
					} 
				/>
			</TwoColumnGrid>
        </StudentProfileCard>

		<StudentProfileCard title='Interests, Activities, and Goals' cardStyling='mt-8'>
			<StudentCardSectionHeader />
			<StudentProfileRowFormat field="Academic/Professional Interests" 
				value={studentData.academicInterests && studentData.academicInterests?.length > 0 ? (
					<div className='mt-2'>{studentData.academicInterests.map((interest: any) => (
						<span
							key={interest.name} // span tag here is not really necessary, but it's good to have "key"
						>
							<StudentTagFormat value={interest.name} />
						</span>
					))}</div>
				) : (
					<span>{DefaultProfileNonReportedField}</span>
				)} />

			<StudentProfileRowFormat field="Personal Interests" 
				value={studentData.personalInterests && studentData.personalInterests?.length > 0 ? (
					<div className='mt-2'>{studentData.personalInterests.map((interest: PersonalInterest) => (
						<span
							key={interest.name}
						>
							<StudentTagFormat value={interest.name} />
						</span>
					))}</div>
				) : (
					<span>{DefaultProfileNonReportedField}</span>
				)} />

				<StudentProfileRowFormat field="Activities" 
				value={studentData.currentActivities && studentData.currentActivities?.length > 0 ? (
					<div className='mt-2'>{studentData.currentActivities.map((activity: Activity) => (
						<span
							key={activity.name}
						>
							<StudentTagFormat value={activity.name} />
						</span>
					))}</div>
				) : (
					<span>{DefaultProfileNonReportedField}</span>
				)} />

				<div className='flex flex-col gap-2'>
					<div>
						<div className="font-bold">Do you plan to attend graduate school:</div>
						{studentData.gradSchoolPlans && studentData.gradSchoolPlans != YesNoMaybeAlready.NotProvided ? <div>{printGradSchoolPlans(studentData.gradSchoolPlans)}</div> : <div>{DefaultProfileNonReportedField}</div>}
					</div>
					<div>
						<div className="font-bold">Would you be interested in learning about applying for graduate programs abroad:</div>
						{studentData.gradSchoolAbroadPlans && studentData.gradSchoolAbroadPlans != YesNoMaybeAlready.NotProvided ? <div>{YesNoMaybeFormatter(studentData.gradSchoolAbroadPlans)}</div> : <div>{DefaultProfileNonReportedField}</div>}
					</div>

					<div>
						<div className="font-bold">Please describe your future academic and professional goals as you envision them right now:</div>
						<div>{studentData.futureGoals?.trim() != '' ? studentData.futureGoals : DefaultProfileNonReportedField}</div>
					</div>

					<div>
						<div className="font-bold">Do you have passion projects you wish you could give more time to, but you don't because you think your major and studies conflict with pursuing them? :</div>
						{studentData.passionProjects ? <div>{studentData.passionProjects}</div> : <div>{DefaultProfileNonReportedField}</div>}
					</div>

					<div>
						<div className="font-bold">Have you learned about the value of "high impact practices" at USF? :</div>
						{studentData.knowsAboutHIPS && studentData.knowsAboutHIPS != YesNoMaybeAlready.NotProvided ? <div>{YesNoMaybeFormatter(studentData.knowsAboutHIPS)}</div> : <div>{DefaultProfileNonReportedField}</div>}
					</div>
				</div>
			</StudentProfileCard>
				
			<StudentProfileCard title='Experiences (Research, Publications, etc.)' cardStyling='mt-8'>
				<StudentCardSectionHeader />
				{(studentData.experienceList) && (studentData.experienceList?.length > 0)
					?<div className="space-y-4">
						<div className='grid w-full grid-col-1 sm:grid-cols-3 grid-rows-1 gap-x-5 gap-y-5'>
						{studentData.experienceList?.map((elem, index) => (
							<StudentExperienceCard index={index} elem={elem} />
						))}
						</div>
					</div>
					:<div>
						<i className='text-slate-500 dark:text-slate-300'>No experiences have been added...</i>  
					</div>}
			</StudentProfileCard>

			

			<StudentProfileCard title='International Education' cardStyling='mt-8 mb-10'>
				<StudentCardSectionHeader />
				<div className='flex flex-col gap-2'>
					<div>
						<div className="font-bold">Do you speak any languages other than English:</div>
						<div>{printSpeakForeignLanguage}</div>
					</div>
					<StudentProfileRowFormat field="Languages" 
						value={studentData.speakForeignLanguageList && studentData.speakForeignLanguageList?.length > 0 ? (
							<div className='mt-2'>{studentData.speakForeignLanguageList.map((language: any) => (
								<span
									key={language.name} // span tag here is not really necessary, but it's good to have "key"
								>
									<StudentTagFormat value={language.name} />
								</span>
							))}</div>
						) : (
							<span>None</span>
					)} />
				</div>
			</StudentProfileCard>

			<StudentProfileCard title='Awards' cardStyling='mt-8'>
			<div className='flex justify-between m-2'>
				{state.user?.admin ? <button className='bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center space-x-2' onClick={() => handleAddAwardModal()} disabled={awardModalLoading}>
					<span>{awardModalLoading ? 'Please wait...' : 'Add a new award'}</span>
					{!awardModalLoading && (<PlusCircle className='mt-1 w-5 h-5'/>)}
				</button> : <div></div>}

				<Button
				  className='gap-2'
				  onClick={() => {setOrderAwardsAscending(!orderAwardsAscending)}}
				  abbr={orderAwardsAscending ? "Click to change display order of awards: show newest first" : "Click to change display order of awards: show oldest first"}
				>
				  <span>{orderAwardsAscending ? 'Showing oldest first' : 'Showing newest first'} </span>
				  {orderAwardsAscending ? <CaretDownFill className='mt-1 w-4 h-4'/> :  <CaretUpFill className='mt-1 w-4 h-4'/>}
				</Button>
			</div>
				{(studentAwards) && (studentAwards.length > 0)
				?<div>
					<div className='space-y-4'>
						<div className='grid w-full grid-col-1 sm:grid-cols-2 grid-rows-1 gap-x-5 gap-y-5'>
							{[...studentAwards]
							.sort((a,b) => {
								// oldest first
								if(orderAwardsAscending) return (a.academicYear ?? 0) - (b.academicYear ?? 0)
								// most recent first
								return (b.academicYear ?? 0) - (a.academicYear ?? 0)
							})
							.map((elem, index) => (
								<StudentAwardCard 
									index={index} 
									elem={elem} 
									isAdmin={!!state.user?.admin} 
									onDelete={(id) => {handleDeleteStudentAward(id)}}
									onEdit={(award) => handleEditAward(award)}
								/>
							))}
						</div>
					</div>
				</div>
				: 
					<div>
						<i className='text-slate-500 dark:text-slate-300'>No awards have been added...</i>  
					</div>}
			</StudentProfileCard>

        <ScrollButtons />
        {state.user?.admin && <StudentNotes studentId={usfid} setNonce={setNonce} nonce={nonce} />}
      </div>}

	  <AddStudentAwardModal 
		isOpen={awardModalOpen}
		onClose={() => {
			setAwardModalOpen(false)
			setEditingAward(null);
		}}
		onSubmit={(form) => handleAwardSubmit(form)}
		awards={awards!}
		programs={programs!}
		colleges={colleges!}
		isModal={true}
		currentStudentID={studentData?.user?.netID}
		initialData={editingAward}
		/>
    </Container>
  );
};
